import React, { useState } from 'react';
import { Bus, Check, Trash2, ToggleLeft, ToggleRight, CalendarDays, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_COMP_KEYS = ['std_seat', 'flip_up', 'barrier'];
const DEFAULT_COMP_LABELS = { std_seat: 'Std Seat', flip_up: 'Flip-Up', barrier: 'Barrier' };

export default function CityBusPlanGrid({ plans, projectId, project, planDate, onRefresh, onDelete, isAdmin = true }) {
  const isCityBus = project?.category === 'City Bus';
  const setPlans = plans
    .filter(p => p.set_number || p.set_numbers)
    .sort((a, b) => (a.set_number || 0) - (b.set_number || 0));
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelValue, setLabelValue] = useState('');
  const [localTrackMap, setLocalTrackMap] = useState({});
  const [rescheduling, setRescheduling] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');

  // Derive component keys - only relevant for City Bus
  const allCompKeys = isCityBus ? DEFAULT_COMP_KEYS : ['std_seat'];
  const compLabels = {};
  allCompKeys.forEach(k => { compLabels[k] = DEFAULT_COMP_LABELS[k] || k; });

  const getTrackComponents = (plan) => {
    if (!isCityBus) return false;
    if (localTrackMap[plan.id] !== undefined) return localTrackMap[plan.id];
    return plan.track_components || false;
  };

  const getCompKeys = (plan) => getTrackComponents(plan) ? allCompKeys : [allCompKeys[0]];

  const toggleSetTrackComponents = async (planId, current) => {
    const newVal = !current;
    setLocalTrackMap(prev => ({ ...prev, [planId]: newVal }));
    try {
      await axios.put(`${API}/api/plans/${planId}`, { track_components: newVal });
      toast.success(newVal ? 'Barrier/Flip-Up tracking ON' : 'Set only mode');
      if (onRefresh) onRefresh();
    } catch {
      setLocalTrackMap(prev => ({ ...prev, [planId]: current }));
      toast.error('Failed to update');
    }
  };

  const toggleComponent = async (planId, comp, field, currentVal) => {
    try {
      await axios.put(`${API}/api/plans/${planId}/component-status`, {
        component: comp, field, value: !currentVal, date: planDate
      });
      if (onRefresh) onRefresh();
    } catch { toast.error('Update failed'); }
  };

  const saveSetLabel = async (planId) => {
    try {
      await axios.put(`${API}/api/plans/${planId}`, { set_label: labelValue });
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed to save set number'); }
    setEditingLabel(null);
  };

  const handleReschedule = async (planId) => {
    if (!rescheduleDate) { toast.error('Select a date'); return; }
    try {
      const res = await axios.post(`${API}/api/plans/${planId}/reschedule`, { new_date: rescheduleDate });
      if (res.data?.partial) {
        toast.success(`Partial reschedule: moved ${res.data.moved_components.join(', ')} to ${rescheduleDate}. ${res.data.kept_components.join(', ')} kept on original date.`, { duration: 5000 });
      } else if (res.data?.status === 'nothing_to_move') {
        toast.info('All components are already done - nothing to move.');
      } else {
        toast.success(`Set rescheduled to ${rescheduleDate}`);
      }
      setRescheduling(null);
      setRescheduleDate('');
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    }
  };

  if (!setPlans.length) return (
    <div className="px-4 py-3 text-center text-[11px] text-slate-400 italic">
      No sets planned for this day. Use the + button to add sets.
    </div>
  );

  // Summary
  const totalSets = setPlans.length;
  const completeSets = isCityBus
    ? setPlans.filter(p => {
        const cs = p.component_status || {};
        const keys = getCompKeys(p);
        return keys.every(ck => cs[ck]?.shipped);
      }).length
    : setPlans.filter(p => p.shipped_quantity > 0).length;

  // City Bus specific summary
  const summary = {};
  if (isCityBus) {
    allCompKeys.forEach(ck => {
      summary[ck] = { done: 0, shipped: 0 };
      setPlans.forEach(p => {
        const cs = p.component_status || {};
        if (cs[ck]?.done) summary[ck].done++;
        if (cs[ck]?.shipped) summary[ck].shipped++;
      });
    });
  }

  const trackedCount = isCityBus ? setPlans.filter(p => getTrackComponents(p)).length : 0;

  // Component shortage for tracked sets (City Bus only)
  const compShortage = {};
  if (isCityBus) {
    const trackedSets = setPlans.filter(p => getTrackComponents(p));
    allCompKeys.forEach(ck => {
      compShortage[ck] = { notDone: 0, notShipped: 0 };
      trackedSets.forEach(p => {
        const cs = p.component_status || {};
        if (!cs[ck]?.done) compShortage[ck].notDone++;
        if (!cs[ck]?.shipped) compShortage[ck].notShipped++;
      });
    });
  }

  // Coach summary
  const coachDone = !isCityBus ? setPlans.filter(p => (p.completed_quantity || 0) > 0).length : 0;
  const coachShipped = !isCityBus ? setPlans.filter(p => (p.shipped_quantity || 0) > 0).length : 0;

  return (
    <div data-testid="citybus-plan-grid">
      {/* Summary */}
      <div className="px-4 py-2 bg-green-50/80 border-b border-green-100 flex items-center gap-3 flex-wrap text-[10px]">
        <span className="font-bold text-slate-700 flex items-center gap-1">
          <Bus className="w-3.5 h-3.5 text-green-600" />
          {completeSets}/{totalSets} sets complete
        </span>
        {isCityBus ? (
          <>
            <span className="text-slate-300">|</span>
            <span className="text-slate-600 font-semibold">Std Seat: <span className="text-emerald-600">{summary.std_seat?.done || 0}D</span> <span className="text-violet-600">{summary.std_seat?.shipped || 0}S</span> <span className="text-slate-400">/{totalSets}</span></span>
            {trackedCount > 0 && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-indigo-600 font-semibold">Flip-Up: <span className="text-emerald-600">{summary.flip_up?.done || 0}D</span> <span className="text-violet-600">{summary.flip_up?.shipped || 0}S</span></span>
                <span className="text-indigo-600 font-semibold">Barrier: <span className="text-emerald-600">{summary.barrier?.done || 0}D</span> <span className="text-violet-600">{summary.barrier?.shipped || 0}S</span></span>
                <span className="text-[9px] text-indigo-400">({trackedCount} sets tracked)</span>
              </>
            )}
          </>
        ) : (
          <>
            <span className="text-slate-300">|</span>
            <span className="text-emerald-600 font-semibold">Done: {coachDone}</span>
            <span className="text-violet-600 font-semibold">Shipped: {coachShipped}</span>
            <span className="text-slate-400">/{totalSets}</span>
          </>
        )}
      </div>
      {/* Component Shortage Summary for tracked sets - City Bus only */}
      {isCityBus && trackedCount > 0 && ((compShortage.flip_up?.notDone || 0) > 0 || (compShortage.barrier?.notDone || 0) > 0 || (compShortage.flip_up?.notShipped || 0) > 0 || (compShortage.barrier?.notShipped || 0) > 0) && (
        <div className="px-4 py-1.5 bg-amber-50/80 border-b border-amber-100 flex items-center gap-3 flex-wrap text-[10px]" data-testid="component-shortage-summary">
          <span className="font-bold text-amber-700">Shortage ({trackedCount} tracked):</span>
          {['flip_up', 'barrier'].map(ck => (
            <span key={ck} className="text-slate-600 font-semibold">
              {compLabels[ck]}:
              {compShortage[ck].notDone > 0 && <span className="text-red-500 ml-1">{compShortage[ck].notDone} not done</span>}
              {compShortage[ck].notShipped > 0 && <span className="text-orange-500 ml-1">{compShortage[ck].notShipped} not shipped</span>}
              {compShortage[ck].notDone === 0 && compShortage[ck].notShipped === 0 && <span className="text-emerald-500 ml-1">All complete</span>}
            </span>
          ))}
        </div>
      )}

      {/* Set Rows */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {setPlans.map(p => {
          const cs = p.component_status || {};
          const tracked = getTrackComponents(p);
          const compKeys = tracked ? allCompKeys : [allCompKeys[0]];

          // Progress calculation
          let pct, isComplete, shippedCount, activeKeys;
          if (isCityBus) {
            activeKeys = compKeys.filter(ck => !cs[ck]?.rescheduled_to && !cs[ck]?.carried_over);
            shippedCount = activeKeys.filter(ck => cs[ck]?.shipped).length;
            pct = activeKeys.length > 0 ? Math.round(shippedCount / activeKeys.length * 100) : 100;
            isComplete = activeKeys.length > 0 ? shippedCount === activeKeys.length : true;
          } else {
            // Coach: set-level done/shipped
            isComplete = (p.shipped_quantity || 0) > 0;
            pct = isComplete ? 100 : (p.completed_quantity || 0) > 0 ? 50 : 0;
            shippedCount = isComplete ? 1 : 0;
            activeKeys = [];
          }

          return (
            <div key={p.id} className={`px-4 py-2 border-b border-slate-100 hover:bg-green-50/30 group ${isComplete ? 'bg-emerald-50/20' : ''}`}
              data-testid={`set-plan-${p.set_number}`}>
              <div className="flex items-center gap-3">
                {/* Set Number + Label */}
                <div className="flex items-center gap-1 min-w-[80px]">
                  <span className="text-[9px] font-medium text-slate-400 shrink-0">#{p.set_number || p.set_numbers?.replace('#','') || '?'}</span>
                  {isAdmin && editingLabel === p.id ? (
                    <input type="text" value={labelValue}
                      onChange={e => setLabelValue(e.target.value)}
                      onBlur={() => saveSetLabel(p.id)}
                      onKeyDown={e => { if (e.key === 'Enter') saveSetLabel(p.id); if (e.key === 'Escape') setEditingLabel(null); }}
                      className="w-16 border border-blue-300 rounded px-1.5 py-0.5 text-[11px] font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      placeholder="Set #" autoFocus data-testid={`set-label-input-${p.set_number}`} />
                  ) : (
                    <span className={`text-xs font-bold text-slate-700 ${isAdmin ? 'cursor-pointer hover:bg-blue-50 px-1.5 py-0.5 rounded border border-transparent hover:border-blue-200' : 'px-1.5 py-0.5'}`}
                      onClick={() => { if (isAdmin) { setEditingLabel(p.id); setLabelValue(p.set_label || ''); } }}
                      title={isAdmin ? 'Click to enter set number' : ''} data-testid={`set-label-${p.set_number}`}>
                      {p.set_label || <span className="text-slate-300 font-normal italic text-[10px]">No #</span>}
                    </span>
                  )}
                </div>

                {/* Per-set track toggle - City Bus only */}
                {isCityBus && isAdmin && (
                  <button onClick={() => toggleSetTrackComponents(p.id, tracked)}
                    className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded border transition-all text-[9px] font-semibold shrink-0 ${tracked ? 'bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-100' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'}`}
                    title={tracked ? 'Tracking Barrier/Flip-Up' : 'Set only - click to track components'}
                    data-testid={`track-toggle-${p.set_number}`}>
                    {tracked ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                  </button>
                )}

                {/* City Bus: Per-component Done/Ship buttons */}
                {isCityBus && compKeys.map(ck => {
                  const comp = cs[ck] || {};
                  const isCarriedOver = comp.carried_over === true;
                  const isRescheduled = !!comp.rescheduled_to;

                  if (isRescheduled) {
                    return (
                      <div key={ck} className="flex items-center gap-1.5 border-l border-slate-100 pl-3 opacity-50">
                        <span className="text-[9px] font-semibold text-slate-400 w-14">{compLabels[ck]}</span>
                        <span className="text-[8px] px-2 py-1 bg-purple-50 text-purple-500 border border-purple-200 rounded font-semibold">
                          → {comp.rescheduled_to}
                        </span>
                      </div>
                    );
                  }

                  if (isCarriedOver) {
                    // Carried over but NOT shipped yet → show Ship button
                    const needsShip = comp.done && !comp.shipped;
                    return (
                      <div key={ck} className={`flex items-center gap-1.5 border-l border-slate-100 pl-3 ${needsShip ? '' : 'opacity-40'}`}>
                        <span className="text-[9px] font-semibold text-slate-400 w-14">{compLabels[ck]}</span>
                        <span className={`w-12 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 ${comp.done ? 'bg-emerald-200 text-emerald-600' : 'bg-slate-100 text-slate-300'}`}>
                          {comp.done ? <><Check className="w-3 h-3" /> D</> : '-'}
                        </span>
                        {needsShip && isAdmin ? (
                          <button onClick={() => toggleComponent(p.id, ck, 'shipped', false)}
                            className="w-12 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 transition-all bg-violet-50 text-violet-400 border border-violet-300 hover:bg-violet-100 animate-pulse"
                            title="Ship this carried-over component" data-testid={`comp-${p.set_number}-${ck}-shipped`}>
                            Ship
                          </button>
                        ) : (
                          <span className={`w-12 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 ${comp.shipped ? 'bg-violet-200 text-violet-600' : 'bg-slate-100 text-slate-300'}`}>
                            {comp.shipped ? <><Check className="w-3 h-3" /> S</> : '-'}
                          </span>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={ck} className="flex items-center gap-1.5 border-l border-slate-100 pl-3">
                      <span className="text-[9px] font-semibold text-slate-500 w-14">{compLabels[ck]}</span>
                      {isAdmin ? (
                        <button onClick={() => toggleComponent(p.id, ck, 'done', comp.done)}
                          className={`w-12 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 transition-all ${comp.done ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-300 border border-emerald-200 hover:bg-emerald-100'}`}
                          title={`Done${comp.done_date ? ` (${comp.done_date})` : ''}`} data-testid={`comp-${p.set_number}-${ck}-done`}>
                          {comp.done ? <><Check className="w-3 h-3" /> D</> : 'Done'}
                        </button>
                      ) : (
                        <span className={`w-12 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 ${comp.done ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-300 border border-emerald-200'}`}>
                          {comp.done ? <><Check className="w-3 h-3" /> D</> : '-'}
                        </span>
                      )}
                      {isAdmin ? (
                        <button onClick={() => toggleComponent(p.id, ck, 'shipped', comp.shipped)}
                          className={`w-12 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 transition-all ${comp.shipped ? 'bg-violet-500 text-white' : 'bg-violet-50 text-violet-300 border border-violet-200 hover:bg-violet-100'}`}
                          title={`Shipped${comp.ship_date ? ` (${comp.ship_date})` : ''}`} data-testid={`comp-${p.set_number}-${ck}-shipped`}>
                          {comp.shipped ? <><Check className="w-3 h-3" /> S</> : 'Ship'}
                        </button>
                      ) : (
                        <span className={`w-12 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 ${comp.shipped ? 'bg-violet-500 text-white' : 'bg-violet-50 text-violet-300 border border-violet-200'}`}>
                          {comp.shipped ? <><Check className="w-3 h-3" /> S</> : '-'}
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Coach: Simple set-level Done/Ship buttons */}
                {!isCityBus && (
                  <div className="flex items-center gap-1.5 border-l border-slate-100 pl-3">
                    {isAdmin ? (
                      <button onClick={async () => {
                        try {
                          await axios.put(`${API}/api/plans/${p.id}`, { completed_quantity: p.completed_quantity > 0 ? 0 : 1 });
                          if (onRefresh) onRefresh();
                        } catch { toast.error('Update failed'); }
                      }}
                        className={`w-14 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 transition-all ${p.completed_quantity > 0 ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-300 border border-emerald-200 hover:bg-emerald-100'}`}
                        data-testid={`coach-done-${p.set_number || p.id}`}>
                        {p.completed_quantity > 0 ? <><Check className="w-3 h-3" /> Done</> : 'Done'}
                      </button>
                    ) : (
                      <span className={`w-14 h-6 rounded text-[8px] font-bold flex items-center justify-center ${p.completed_quantity > 0 ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-300'}`}>
                        {p.completed_quantity > 0 ? 'Done' : '-'}
                      </span>
                    )}
                    {isAdmin ? (
                      <button onClick={async () => {
                        try {
                          await axios.put(`${API}/api/plans/${p.id}`, { shipped_quantity: p.shipped_quantity > 0 ? 0 : 1, completed_quantity: 1 });
                          if (onRefresh) onRefresh();
                        } catch { toast.error('Update failed'); }
                      }}
                        className={`w-14 h-6 rounded text-[8px] font-bold flex items-center justify-center gap-0.5 transition-all ${p.shipped_quantity > 0 ? 'bg-violet-500 text-white' : 'bg-violet-50 text-violet-300 border border-violet-200 hover:bg-violet-100'}`}
                        data-testid={`coach-ship-${p.set_number || p.id}`}>
                        {p.shipped_quantity > 0 ? <><Check className="w-3 h-3" /> Ship</> : 'Ship'}
                      </button>
                    ) : (
                      <span className={`w-14 h-6 rounded text-[8px] font-bold flex items-center justify-center ${p.shipped_quantity > 0 ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-300'}`}>
                        {p.shipped_quantity > 0 ? 'Ship' : '-'}
                      </span>
                    )}
                  </div>
                )}

                {/* Progress + Actions */}
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${isComplete ? 'bg-emerald-100 text-emerald-700' : shippedCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>{pct}%</span>
                  {isAdmin && (
                    <button onClick={() => { setRescheduling(p.id); setRescheduleDate(''); }}
                      className="p-1 hover:bg-blue-50 rounded border border-transparent hover:border-blue-200 transition-colors"
                      title="Reschedule to another day"
                      data-testid={`reschedule-set-${p.set_number}`}>
                      <CalendarDays className="w-3.5 h-3.5 text-blue-400" />
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => onDelete(p.id)}
                      className="p-1 hover:bg-red-50 rounded border border-transparent hover:border-red-200 transition-colors"
                      data-testid={`delete-set-${p.set_number}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  )}
                </div>
              </div>
              {/* Date info - City Bus only */}
              {isCityBus && compKeys.some(ck => (cs[ck] || {}).done_date || (cs[ck] || {}).ship_date) && (
                <div className="ml-12 mt-1 flex gap-3 text-[8px] text-slate-400">
                  {compKeys.map(ck => {
                    const comp = cs[ck] || {};
                    if (!comp.done_date && !comp.ship_date) return null;
                    return (
                      <span key={ck}>
                        {compLabels[ck]}: {comp.done_date && <span className="text-emerald-500">D:{comp.done_date}</span>}
                        {comp.ship_date && <span className="text-violet-500 ml-1">S:{comp.ship_date}</span>}
                      </span>
                    );
                  })}
                </div>
              )}
              {/* Inline Reschedule */}
              {rescheduling === p.id && (
                <div className="ml-12 mt-1.5 flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg" data-testid={`reschedule-panel-${p.set_number}`}>
                  <span className="text-[10px] font-semibold text-blue-700">Move to:</span>
                  <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                    className="border border-blue-300 rounded px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-blue-400"
                    data-testid={`reschedule-date-${p.set_number}`} />
                  <button onClick={() => handleReschedule(p.id)}
                    className="px-2.5 py-1 text-[10px] font-bold bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    disabled={!rescheduleDate} data-testid={`reschedule-confirm-${p.set_number}`}>
                    Reschedule
                  </button>
                  <button onClick={() => { setRescheduling(null); setRescheduleDate(''); }}
                    className="p-0.5 hover:bg-blue-100 rounded" data-testid={`reschedule-cancel-${p.set_number}`}>
                    <X className="w-3 h-3 text-blue-400" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
